import { useState, useEffect } from 'react';
import { ChevronRight, Search, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useGetActionLogsList } from "../../hooks/useActionLogs"
import moment from 'moment';

const ActionLogs = () => {
    const { action_type } = useParams();
    const [actionType, setActionType] = useState('like')
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [actionLogsList, setActionLogsList] = useState([]);
    const { data: actionLogsListResponse, isSuccess: isActionLogsListSuccess } = useGetActionLogsList(`?action_type=${actionType}&page=${currentPage}&limit=${rowsPerPage}&search_key=${searchTerm}`)

    useEffect(() => {
        if (isActionLogsListSuccess) {
            setActionLogsList(actionLogsListResponse?.data?.data?.result)
            let tp = Math.ceil((actionLogsListResponse?.data?.data?.totalCount) / rowsPerPage)
            setTotalPages(tp == 0 ? 1 : tp)
        }
    }, [actionLogsListResponse, isActionLogsListSuccess])

    useEffect(() => {
        if(action_type) {
            let newActionType = 'like'
            switch(action_type){
                case 'like' :
                    newActionType = 'swipe_right';
                    break;
                case 'superlike' :
                    newActionType = 'super_swipe';
                    break;
                case 'pass-reject' :
                    newActionType = 'pass_reject';
                    break;
                case 'profile' :
                    newActionType = 'view_profile';
                    break;
                case 'match' :
                    newActionType = 'match';
                    break;
                default:
                    newActionType = 'like';
            }
            setActionType(newActionType)
        }
    }, [action_type])

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Breadcrumb */}
                <div className="bg-white border-b border-gray-200 px-8 py-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>Dashboard</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-900">Action Logs</span>
                        <ChevronRight className="w-4 h-4" />
                        <span className="text-gray-900">{action_type}</span>
                    </div>
                </div>
                {/* Page Content */}
                <div className="flex-1 px-1 py-4">
                    <div className="max-w-7xl">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">Action Logs</h1>
                                <p className="text-gray-600">Track all activities between users</p>
                            </div>
                        </div>
                        {/* Search */}
                        <div className="mb-6">
                            <div className="relative max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search by name of users"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        {/* Table */}
                        <div className="bg-white rounded-lg border border-gray-200">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            S.No
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            TimeStamp
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action By
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action On
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {actionLogsList?.map((user, index) => (
                                        <tr key={user?._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                {index + 1}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{moment(user?.createdAt?.role).format('LLL')}</td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{`${user?.action_by?.first_name} ${user?.action_by?.last_name}`}</td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{`${user?.action_on?.first_name} ${user?.action_on?.last_name}`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {/* Pagination */}
                            <div className="flex justify-end m-4">
                                <div className="flex items-center">
                                    <div className="mr-4 flex justify-between">
                                        <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                                        <select
                                            className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                            value={rowsPerPage}
                                            onChange={(e) => { setCurrentPage(1); setRowsPerPage(Number(e.target.value)) }}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="mr-4">Page {currentPage} of {totalPages}</span>
                                        <div className="flex">
                                            <button
                                                className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}>
                                                <ChevronsLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}>
                                                <ChevronLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}>
                                                <ChevronRight size={16} />
                                            </button>
                                            <button
                                                className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}>
                                                <ChevronsRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ActionLogs;